import React, { useState, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  addPersonalInfoAdminThunk,
  updatePersonalInfoAdminThunk,
  addLicIdentificationAdminThunk,
  updateLicIdentificationAdminThunk,
  getIdentificationAdminThunk,
  addLicEmploymentAdminThunk,
  updateLicEmploymentAdminThunk,
  fetchLicEmploymentAdminThunk,
  fetchLicOtherPolicyAdminThunk,
  addLicInsuranceHistoryAdminThunk,
  updateLicOtherPolicyAdminThunk,
  addLicNomineeAdminThunk,
  updateLicNomineeAdminThunk,
  addLicHealthAdminThunk,
  updateLicHealthAdminThunk,
  addLicLegalAdminThunk,
  updateLicLegalAdminThunk,
  getLicDetailByIdThunk,
  updateNewLicProposalAdminThunk,
  fetchLicNomineeAdminThunk,
  fetchLicHealthAdminThunk,
  fetchLicLegalAdminThunk,
  fetchLicDocumentsAdminThunk,
} from "slices/KYC/thunk";
import {
  setProposalId,
  setLicId,
  setIdentificationId,
  setIdentificationData,
  setEmploymentId,
  setEmploymentData,
  setOtherPolicyId,
  setNomineeId,
  setHealthId,
  setLegalId,
  setDocumentId,
  resetStepperIds,
} from "slices/KYC/reducer";
import { extractIdsFromLicDetail, shouldUpdate } from "./utils/stepperIds";
import ProposalFormLayout from "./components/ProposalFormLayout";
import ProposalStepper, { STEPS } from "./components/ProposalStepper";
import ProposalFormFooter from "./components/ProposalFormFooter";
import Step1ProposalDetails from "./steps/Step1ProposalDetails";
import Step2PersonalInfo from "./steps/Step2PersonalInfo";
import Step3Identification from "./steps/Step3Identification";
import Step4Employment from "./steps/Step4Employment";
import Step5InsuranceHistory from "./steps/Step5InsuranceHistory";
import Step6Nominee from "./steps/Step6Nominee";
import Step7HealthDetails from "./steps/Step7HealthDetails";
import Step8Declaration from "./steps/Step8Declaration";
import Step9Submit from "./steps/Step9Submit";
import StepPlaceholder from "./steps/StepPlaceholder";
import { ProposalFormValues } from "./types/proposalForm.types";
import "./styles/proposalForm.scss";

// Validation schema - can be expanded per step
const getValidationSchema = (currentStep: number, skipMandatory: boolean) => {
  if (skipMandatory) {
    return Yup.object({});
  }

  const baseSchema: any = {};

  // Step 1 validations
  if (currentStep === 1) {
    baseSchema.inward_number = Yup.string().required("Required");
    baseSchema.date_of_inward = Yup.string().required("Required");
    baseSchema.date_of_proposal = Yup.string().required("Required");
    baseSchema.consultant_name = Yup.string().required("Required");
    baseSchema.consultant_code = Yup.string().required("Required");
    baseSchema.chief_agent_name = Yup.string().required("Required");
    baseSchema.chief_agent_number = Yup.string().required("Required");
    baseSchema.proposal_deposit_currency = Yup.string().required("Required");
    baseSchema.proposal_deposit_amount = Yup.mixed()
      .test("is-number", "Must be a number", (value) => {
        if (!value) return false;
        return !isNaN(Number(value));
      })
      .required("Required");
    baseSchema.proposal_number = Yup.string().required("Required");
  }

  // Step 2 validations
  if (currentStep === 2) {
    baseSchema.first_name = Yup.string().required("Required");
    baseSchema.last_name = Yup.string().required("Required");
    baseSchema.nationality = Yup.string().required("Required");
    baseSchema.country_of_birth = Yup.string().required("Required");
    baseSchema.place_of_birth = Yup.string().required("Required");
    baseSchema.date_of_birth = Yup.string().required("Required");
    baseSchema.gender = Yup.string().required("Required");
    baseSchema.address = Yup.string().required("Required");
  }

  // Step 3 validations
  if (currentStep === 3) {
    baseSchema.visaissue_country = Yup.string().required("Required");
    baseSchema.passport_copy = Yup.mixed().required("Required");
    baseSchema.emirates_id_copy = Yup.mixed().required("Required");
  }

  // Step 4 validations
  if (currentStep === 4) {
    baseSchema.occupation = Yup.string().required("Required");
    baseSchema.industry_type = Yup.string().required("Required");
    baseSchema.employername = Yup.string().required("Required");
    baseSchema.income_currency = Yup.string().required("Required");
    baseSchema.total_anual_income = Yup.mixed()
      .test("is-number", "Must be a number", (value) => {
        if (!value) return false;
        return !isNaN(Number(value));
      })
      .required("Required");
    baseSchema.employer_address = Yup.string().required("Required");
    baseSchema.income_source = Yup.string().required("Required");
    baseSchema.risk_assessment = Yup.string().required("Required");
    baseSchema.risk_assessment_details = Yup.string().when("risk_assessment", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
  }

  if (currentStep === 5) {
    baseSchema.bank_account_details = Yup.string().required("Required");
    baseSchema.bank_name = Yup.string().when("bank_account_details", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
    baseSchema.bank_branch = Yup.string().when("bank_account_details", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
    baseSchema.iban = Yup.string().when("bank_account_details", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
    baseSchema.account_number = Yup.string().when("bank_account_details", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
    baseSchema.relative_name = Yup.string().when("close_relatives_position", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
    baseSchema.relative_relationship = Yup.string().when("close_relatives_position", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
    baseSchema.relative_position = Yup.string().when("close_relatives_position", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
  }

  if (currentStep === 6) {
    // Nominee fields are optional, but if provided, validate them
    // This will be handled in the component itself
  }

  if (currentStep === 7) {
    baseSchema.height = Yup.string().required("Required");
    baseSchema.weight = Yup.string().required("Required");
    baseSchema.smoking_drinking_habits = Yup.string().required("Required");
    baseSchema.smokingstop_date = Yup.string().when("smoking_drinking_habits", {
      is: "Yes",
      then: (schema) => schema.required("Required"),
      otherwise: (schema) => schema.notRequired(),
    });
  }

  if (currentStep === 8) {
    baseSchema.declartion_accepted = Yup.number().oneOf([1], "You must agree to the terms and conditions");
    baseSchema.notfatca_holding = Yup.number().oneOf([1], "You must agree to the FATCA declaration");
    baseSchema.isverify_consent = Yup.number().oneOf([1], "You must provide authorization consent");
    baseSchema.date_of_sign = Yup.string().required("Required");
    baseSchema.placeof_sign = Yup.string().required("Required");
  }

  if (currentStep === 9) {
    baseSchema.identity_proof = Yup.mixed().required("Required");
    baseSchema.address_proof = Yup.mixed().required("Required");
    baseSchema.income_proof = Yup.mixed().required("Required");
    baseSchema.review_confirmation = Yup.boolean().oneOf([true], "You must confirm that all information is correct");
  }

  return Yup.object(baseSchema);
};

const getDefaultCountryCode = () => {
  return process.env.NODE_ENV === 'production' ? '+973' : '+91';
};

/** Format API datetime to YYYY-MM-DD for date inputs. */
function toDateStr(v: any): string | undefined {
  if (v == null || v === "") return undefined;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

/** Map getlicdetailbyid response to form values. API shape: { data?: record } or record; record has personal_detail, otherpolicies, etc. */
function mapLicDetailToFormValues(payload: any): Partial<ProposalFormValues> {
  if (!payload || typeof payload !== "object") return {};
  const d = payload?.data != null ? payload.data : payload;
  const pd = d.personal_detail ?? d;
  const ident = pd.identity_detail ?? d.identity_detail ?? {};
  const empList = Array.isArray(pd.employments) ? pd.employments : [];
  const emp = empList[0] ?? {};
  const toStr = (v: any) => (v != null && v !== "" ? String(v) : undefined);
  const toNum = (v: any) => (v != null && v !== "" ? Number(v) : undefined);
  const toDocId = (v: any): number | undefined => (v != null && v !== "" ? Number(v) : undefined);
  // Use only d.depositamttype for depositamttype — do not use payment_mode so user's value is not overwritten with "Cash"
  return {
    inward_number: toStr(d.inward_number),
    date_of_inward: toDateStr(d.date_of_inward) ?? toStr(d.date_of_inward),
    date_of_proposal: toDateStr(d.date_of_proposal) ?? toStr(d.date_of_proposal),
    consultant_name: toStr(d.consultant_name),
    consultant_code: toStr(d.consultant_code),
    chief_agent_name: toStr(d.chief_agent_name),
    chief_agent_number: toStr(d.chief_agent_number),
    proposal_deposit_currency: toStr(d.proposal_deposit_currency),
    proposal_deposit_amount: toNum(d.proposal_deposit_amount) ?? d.proposal_deposit_amount ?? "",
    proposal_number: toStr(d.proposal_number),
    depositamttype: toStr(d.depositamttype),
    first_name: toStr(pd.first_name),
    middle_name: toStr(pd.middle_name),
    last_name: toStr(pd.last_name),
    father_full_name: toStr(pd.father_full_name),
    nationality: toStr(pd.nationality),
    country_of_birth: toStr(pd.country_of_birth),
    place_of_birth: toStr(pd.place_of_birth),
    date_of_birth: toDateStr(pd.date_of_birth) ?? toStr(pd.date_of_birth),
    gender: pd.gender === "Male" || pd.gender === "Female" || pd.gender === "Others" ? pd.gender : undefined,
    address: toStr(pd.address),
    permanent_address: toStr(pd.permanent_address),
    local_country_code: toStr(pd.local_country_code),
    local_mobile_number: toStr(pd.local_mobile_number),
    local_office_number: toStr(pd.local_office_number),
    local_residence_number: toStr(pd.local_residence_number),
    home_country_code: toStr(pd.home_country_code),
    home_mobile_number: toStr(pd.home_mobile_number),
    home_office_number: toStr(pd.home_office_number),
    home_residence_number: toStr(pd.home_residence_number),
    homeemail: toStr(pd.homeemail ?? pd.homeemail),
    passsportnumber: toStr(ident.passsportnumber),
    passport_doe: toDateStr(ident.passport_doe) ?? toStr(ident.passport_doe),
    countryofissue: toStr(ident.countryofissue),
    citizenshipnumber: toStr(ident.citizenshipnumber),
    citizenship_doe: toDateStr(ident.citizenship_doe) ?? toStr(ident.citizenship_doe),
    visaissue_country: toStr(ident.visaissue_country),
    visa_doe: toDateStr(ident.visa_doe) ?? toStr(ident.visa_doe),
    passport_copy: toStr(ident.passport) ?? null,
    emirates_id_copy: toStr(ident.emiratesid) ?? null,
    passport_copy_document_id: toDocId(ident.passport_copy_document_id ?? ident.passport_document_id),
    emirates_id_copy_document_id: toDocId(ident.emirates_id_copy_document_id ?? ident.emirates_id_document_id),
    occupation: toStr(emp.occupation),
    industry_type: toStr(emp.industry_type),
    employername: toStr(emp.employername),
    income_currency: toStr(emp.income_currency),
    total_anual_income: toNum(emp.total_anual_income) ?? toStr(emp.total_anual_income),
    employer_address: toStr(emp.employer_address),
    income_source: toStr(emp.income_source),
    risk_assessment: emp.isoccupationrisk === 1 || emp.isoccupationrisk === "Yes" ? "Yes" : emp.isoccupationrisk === 0 || emp.isoccupationrisk === "No" ? "No" : "", // API: isoccupationrisk 1=Yes, 0=No
    occupationremarks: toStr(emp.occupationremarks),
    // Step 5: from otherpolicies (first item for section fields; all otherpolicy_detail for insurance_plans)
    ...mapOtherPoliciesToStep5(d),
    // Step 6: nominee_details from API → form nomineeDetails (first element when single-nominee form)
    ...((): Partial<ProposalFormValues> => {
      const nomineeList = mapNomineeDetailsFromResponse(pd.nominee_details ?? d.nominee_details);
      return nomineeList.length > 0 ? { nomineeDetails: nomineeList } : {};
    })(),
    ...(() => {
      const healthList = Array.isArray(pd.health_details) ? pd.health_details : Array.isArray(d.health_details) ? d.health_details : [];
      return healthList[0] ? mapHealthDataToFormValues(healthList[0]) : {};
    })(),
    ...(() => {
      const legal = pd.legal_details && typeof pd.legal_details === "object" ? pd.legal_details : d.legal_details;
      if (!legal || typeof legal !== "object") return {};
      const toNum01 = (v: any): 0 | 1 => (v === 1 || v === "1" ? 1 : 0);
      const toLegalStr = (v: any) => (v != null && v !== "" ? String(v) : undefined);
      return {
        declartion_accepted: toNum01(legal.declartion_accepted),
        notfatca_holding: toNum01(legal.notfatca_holding),
        isverify_consent: toNum01(legal.isverify_consent),
        digital_sign: toLegalStr(legal.digital_sign) ?? "",
        date_of_sign: toDateStr(legal.date_of_sign) ?? toLegalStr(legal.date_of_sign) ?? "",
        placeof_sign: toLegalStr(legal.placeof_sign) ?? "",
      };
    })(),
  };
}

/** Normalize date string to YYYY-MM-DD for form. */
function toDateStrForPlan(dateVal: any): string {
  if (dateVal == null || dateVal === "") return "";
  const s = String(dateVal);
  return s.match(/^\d{4}-\d{2}-\d{2}/) ? s.slice(0, 10) : s;
}

/** Convert API date (ISO or any) to YYYY-MM-DD for form inputs. */
function toFormDateStr(dateVal: any): string {
  if (dateVal == null || dateVal === "") return "";
  const s = String(dateVal).trim();
  if (!s) return "";
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return isoMatch[0];
  return s.slice(0, 10);
}

/** Map API nominee_details array to form nomineeDetails. Handles null, ISO dates, first element when single nominee. */
function mapNomineeDetailsFromResponse(nominee_details: any): ProposalFormValues["nomineeDetails"] {
  const list = Array.isArray(nominee_details) ? nominee_details : [];
  if (list.length === 0) return [];
  const toStr = (v: any) => (v != null && v !== "" ? String(v) : "");
  const toMinor = (v: any): 0 | 1 => (v === 1 || v === "1" || v === "Yes" ? 1 : 0);
  return list.map((n: any) => ({
    nominee_name: toStr(n.nominee_name),
    relationship: toStr(n.relationship),
    nominee_dob: toFormDateStr(n.nominee_dob),
    nominee_nationality: toStr(n.nominee_nationality),
    passport_number: toStr(n.passport_number),
    passport_expiry: toFormDateStr(n.passport_expiry),
    nominee_identity_no: toStr(n.nominee_identity_no),
    nominee_identity_expiry: (n.nominee_identity_expiry != null && n.nominee_identity_expiry !== "") ? toFormDateStr(n.nominee_identity_expiry) : null,
    country_code: toStr(n.country_code) || "",
    nominee_local_contactno: toStr(n.nominee_local_contactno),
    nominee_mobileno: toStr(n.nominee_mobileno),
    nominee_address: toStr(n.nominee_address),
    is_minor: toMinor(n.is_minor),
    appointee_name: toStr(n.appointee_name),
    realtion_with_nominee: toStr(n.realtion_with_nominee),
    appointee_dob: (n.appointee_dob != null && n.appointee_dob !== "") ? toFormDateStr(n.appointee_dob) : null,
    appointee_nationality: toStr(n.appointee_nationality),
    appointee_passportno: toStr(n.appointee_passportno),
    appointee_passport_expiry: (n.appointee_passport_expiry != null && n.appointee_passport_expiry !== "") ? toFormDateStr(n.appointee_passport_expiry) : null,
    appointee_identityno: toStr(n.appointee_identityno),
    appintee_identity_expiry: (n.appintee_identity_expiry != null && n.appintee_identity_expiry !== "") ? toFormDateStr(n.appintee_identity_expiry) : null,
    appointee_countycode: toStr(n.appointee_countycode),
    appointee_mobileno: toStr(n.appointee_mobileno),
    appointee_address: toStr(n.appointee_address),
    appintee_digital_sign: toStr(n.appintee_digital_sign),
  }));
}

function mapStep9DocumentsFromList(list: any[]): Partial<ProposalFormValues> {
  if (!Array.isArray(list) || list.length === 0) return {};
  const fieldByName: Record<string, "identity_proof" | "address_proof" | "income_proof" | "medical_reports"> = {
    "identity proof": "identity_proof",
    "address proof": "address_proof",
    "income proof": "income_proof",
    "medical reports": "medical_reports",
  };
  const sorted = [...list].sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0));
  const out: Partial<ProposalFormValues> = {};
  for (const row of sorted) {
    const name = String(row?.document_name ?? row?.document_typename ?? "").trim().toLowerCase();
    const field = fieldByName[name];
    if (!field) continue;
    const url = row?.document_url;
    if (url == null || url === "") continue;
    (out as any)[field] = String(url);
    (out as any)[`${field}_document_id`] = row?.id != null ? Number(row.id) : null;
  }
  return out;
}

/** Map listlichealth_admin response.data[0] to Step7 form state. Handles null, ISO dates, is_smoke → smoking_drinking_habits. */
function mapHealthDataToFormValues(record: any): Partial<ProposalFormValues> {
  if (!record || typeof record !== "object") return {};
  const toStr = (v: any) => (v != null && v !== "" ? String(v) : "");
  const toMinorNum = (v: any): 0 | 1 => (v === 1 || v === "1" ? 1 : 0);
  const smokingHabit = record.is_smoke === 1 || record.is_smoke === "1" ? "Yes" : record.is_smoke === 0 || record.is_smoke === "0" ? "No" : "";
  return {
    height: toStr(record.height),
    weight: toStr(record.weight),
    bmi: toStr(record.bmi),
    is_minor: toMinorNum(record.is_minor),
    smoking_drinking_habits: smokingHabit,
    smokingstop_date: toFormDateStr(record.smokingstop_date),
    medicalhistory: toStr(record.medicalhistory),
    medical_his_detail: toStr(record.medical_his_detail),
    dateof_diagnosis: toFormDateStr(record.dateof_diagnosis),
    treatment_status: toStr(record.treatment_status),
    family_medical_history: toStr(record.family_medical_history),
    is_preganant: toMinorNum(record.is_preganant),
  };
}

/** Map API otherpolicies[] (or other_policy / flat list) to Step 5 form fields and insurance_plans. d is the record (same as in mapLicDetailToFormValues). */
function mapOtherPoliciesToStep5(d: any): Partial<ProposalFormValues> {
  // Support multiple API key shapes: otherpolicies (nested), other_policy (can be flat or nested)
  const pd = d?.personal_detail ?? d;
  const rawList = pd?.otherpolicies ?? d?.otherpolicies ?? d?.other_policy ?? d?.otherpolicy_list;
  const list = Array.isArray(rawList) ? rawList : [];
  const first = list[0];
  const out: Partial<ProposalFormValues> = {};
  if (first && typeof first === "object") {
    const toStr = (v: any) => (v != null && v !== "" ? String(v) : undefined);
    const setIfPresent = (key: keyof ProposalFormValues, value: any) => {
      const s = toStr(value);
      if (s !== undefined) (out as any)[key] = s;
    };
    setIfPresent("premium_waiver_benefit", first.premium_waiver);
    setIfPresent("family_protection_benefit", first.family_protection);
    setIfPresent("close_relatives_position", first.gov_position_hold);
    setIfPresent("relative_name", first.person_name);
    setIfPresent("relative_relationship", first.relationship);
    setIfPresent("relative_position", first.position_hold);
    setIfPresent("bank_account_details", first.is_bankacccount);
    setIfPresent("bank_name", first.bank_name);
    setIfPresent("bank_branch", first.branch_name);
    setIfPresent("iban", first.iban_number);
    setIfPresent("account_number", first.account_number);
    setIfPresent("uae_bank_details", first.uae_cusomer_que);
  }
  const plans: Array<{ id: string; planname: string; policy_term: string; premium_term: string; payment_mode: string; sum_asured: string; premium_amount: string; topup_amount: string; double_accident_befit: string; dateofcommencement: string }> = [];
  const details = Array.isArray(first?.otherpolicy_detail) ? first.otherpolicy_detail : [];
  for (const detail of details) {
    const id = detail?.id != null ? String(detail.id) : detail?.policy_id != null ? String(detail.policy_id) : String(plans.length);
    const dateStr = toDateStrForPlan(detail?.dateofcommencement);
    plans.push({
      id,
      planname: detail?.planname != null ? String(detail.planname) : "",
      policy_term: detail?.policy_term != null ? String(detail.policy_term) : "",
      premium_term: detail?.premium_term != null ? String(detail.premium_term) : "",
      payment_mode: detail?.payment_mode != null ? String(detail.payment_mode) : "",
      sum_asured: detail?.sum_asured != null ? String(detail.sum_asured) : "",
      premium_amount: detail?.premium_amount != null ? String(detail.premium_amount) : "",
      topup_amount: detail?.topup_amount != null ? String(detail.topup_amount) : "",
      double_accident_befit: detail?.double_accident_befit != null ? String(detail.double_accident_befit) : "",
      dateofcommencement: dateStr,
    });
  }
  if (plans.length > 0) out.insurance_plans = plans;
  return out;
}

const initialValues: ProposalFormValues = {
  inward_number: "",
  date_of_inward: "",
  date_of_proposal: "",
  consultant_name: "",
  consultant_code: "",
  chief_agent_name: "",
  chief_agent_number: "",
  proposal_deposit_currency: "",
  proposal_deposit_amount: "",
  proposal_number: "",
  payment_mode: "Cash",
  depositamttype: "",
  skip_mandatory: false,
  
  first_name: "",
  middle_name: "",
  last_name: "",
  father_full_name: "",
  nationality: "",
  country_of_birth: "",
  place_of_birth: "",
  date_of_birth: "",
  gender: "Male",
  address: "",
  permanent_address: "",
  same_as_communication_address: false,
  local_country_code: getDefaultCountryCode(),
  local_mobile_number: "",
  local_office_number: "",
  local_residence_number: "",
  home_country_code: getDefaultCountryCode(),
  home_mobile_number: "",
  home_office_number: "",
  home_residence_number: "",
  homeemail: "",
  
  passsportnumber: "",
  passport_doe: "",
  countryofissue: "",
  citizenshipnumber: "",
  citizenship_doe: "",
  visaissue_country: "",
  visa_doe: "",
  passport_copy: null,
  passport_copy_document_id: null,
  emirates_id_copy: null,
  emirates_id_copy_document_id: null,
  
  occupation: "",
  industry_type: "",
  employername: "",
  income_currency: "",
  total_anual_income: "",
  employer_address: "",
  income_source: "",
  risk_assessment: "",
  risk_assessment_details: "",
  occupationremarks: "",
  insurance_plans: [],
  premium_waiver_benefit: "",
  family_protection_benefit: "",
  close_relatives_position: "",
  relative_name: "",
  relative_relationship: "",
  relative_position: "",
  bank_account_details: "",
  bank_name: "",
  bank_branch: "",
  iban: "",
  account_number: "",
  uae_bank_details: "",
  
  nomineeDetails: [
    {
      nominee_name: "",
      relationship: "",
      nominee_dob: "",
      nominee_nationality: "",
      passport_number: "",
      passport_expiry: "",
      nominee_identity_no: "",
      nominee_identity_expiry: null as string | null,
      country_code: getDefaultCountryCode(),
      nominee_local_contactno: "",
      nominee_mobileno: "",
      nominee_address: "",
      is_minor: 0,
      appointee_name: "",
      realtion_with_nominee: "",
      appointee_dob: null as string | null,
      appointee_nationality: "",
      appointee_passportno: "",
      appointee_passport_expiry: null as string | null,
      appointee_identityno: "",
      appintee_identity_expiry: null as string | null,
      appointee_countycode: "",
      appointee_mobileno: "",
      appointee_address: "",
      appintee_digital_sign: "",
    },
  ],
  
  height: "",
  weight: "",
  bmi: "",
  is_minor: 0,
  smoking_drinking_habits: "",
  smokingstop_date: "",
  medicalhistory: "",
  medical_his_detail: "",
  dateof_diagnosis: "",
  treatment_status: "",
  family_medical_history: "",
  is_preganant: 0,
  
  declartion_accepted: 0,
  notfatca_holding: 0,
  isverify_consent: 0,
  digital_sign: "",
  date_of_sign: "",
  placeof_sign: "",
  
  identity_proof: null,
  identity_proof_document_id: null,
  address_proof: null,
  address_proof_document_id: null,
  income_proof: null,
  income_proof_document_id: null,
  medical_reports: null,
  medical_reports_document_id: null,
  review_confirmation: false,
};

const ProposalFormContainer: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const proposalId = useSelector((state: any) => state.kyc?.proposalId ?? null);
  const licId = useSelector((state: any) => state.kyc?.licId ?? null);
  const licDetailData = useSelector((state: any) => state.kyc?.licDetailData ?? null);
  const otherPolicyData = useSelector((state: any) => state.kyc?.otherPolicyData ?? []);
  const otherPolicyId = useSelector((state: any) => state.kyc?.otherPolicyId ?? null);
  const identificationId = useSelector((state: any) => state.kyc?.identificationId ?? null);
  const identificationData = useSelector((state: any) => state.kyc?.identificationData ?? null);
  const employmentId = useSelector((state: any) => state.kyc?.employmentId ?? null);
  const employmentData = useSelector((state: any) => state.kyc?.employmentData ?? []);
  const nomineeId = useSelector((state: any) => state.kyc?.nomineeId ?? null);
  const nomineeData = useSelector((state: any) => state.kyc?.nomineeData ?? []);
  const healthId = useSelector((state: any) => state.kyc?.healthId ?? null);
  const healthData = useSelector((state: any) => state.kyc?.healthData ?? []);
  const legalId = useSelector((state: any) => state.kyc?.legalId ?? null);
  const legalData = useSelector((state: any) => state.kyc?.legalData ?? []);
  const documentData = useSelector((state: any) => state.kyc?.documentData ?? []);
  const [currentStep, setCurrentStep] = useState(1);
  const [skipMandatory, setSkipMandatory] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailPrefilled, setDetailPrefilled] = useState(false);
  const lastPrefilledProposalIdRef = React.useRef<string | null>(null);
  const lastAppliedOtherPolicyKeyRef = React.useRef<string | null>(null);
  const proposalIdFromUrl = searchParams.get("proposal_id");
  const licIdFromUrl = searchParams.get("lic_id");

  useEffect(() => {
    lastPrefilledProposalIdRef.current = null;
    lastAppliedOtherPolicyKeyRef.current = null;
    setDetailPrefilled(false);
    dispatch(resetStepperIds());
    if (proposalIdFromUrl) {
      const proposalIdNum = Number(proposalIdFromUrl);
      if (!isNaN(proposalIdNum)) {
        dispatch(getLicDetailByIdThunk(proposalIdNum) as any);
      }
    } else if (licIdFromUrl) {
      const id = Number(licIdFromUrl);
      if (!isNaN(id)) dispatch(setLicId(id));
    }
  }, [proposalIdFromUrl, licIdFromUrl, dispatch]);

  // Extract and store all IDs from getlicdetailbyid so each step can decide ADD vs UPDATE
  useEffect(() => {
    if (!licDetailData || detailPrefilled) return;
    const proposalIdFromUrl = searchParams.get("proposal_id");
    const raw = (licDetailData as any)?.data != null ? (licDetailData as any).data : licDetailData;
    const loadedProposalId = raw?.id ?? raw?.proposal_id;
    if (proposalIdFromUrl && loadedProposalId != null && String(loadedProposalId) !== String(proposalIdFromUrl)) {
      return;
    }
    const ids = extractIdsFromLicDetail(licDetailData);
    dispatch(setProposalId(ids.proposal_id));
    dispatch(setLicId(ids.lic_id));
    dispatch(setIdentificationId(ids.identification_id));
    dispatch(setEmploymentId(ids.employment_id));
    dispatch(setOtherPolicyId(ids.policy_id));
    dispatch(setNomineeId(ids.nominee_id));
    dispatch(setHealthId(ids.health_id));
    dispatch(setLegalId(ids.legal_id));
    dispatch(setDocumentId(ids.document_id));

    const d = licDetailData as any;
    const pd = d?.personal_detail ?? d;
    const identityDetail = pd?.identity_detail ?? d?.identity_detail;
    if (identityDetail && typeof identityDetail === "object") {
      dispatch(setIdentificationData(identityDetail));
    }
    const empList = Array.isArray(pd?.employments) ? pd.employments : [];
    if (empList.length > 0) {
      dispatch(setEmploymentData(empList));
    }
    if (ids.lic_id != null) {
      dispatch(fetchLicOtherPolicyAdminThunk(ids.lic_id) as any);
    }
    setDetailPrefilled(true);
  }, [licDetailData, dispatch, detailPrefilled, searchParams]);

  useEffect(() => {
    if (currentStep === 3 && licId) {
      dispatch(getIdentificationAdminThunk(licId) as any);
    }
  }, [currentStep, licId, dispatch]);

  useEffect(() => {
    if (currentStep === 4 && licId) {
      dispatch(fetchLicEmploymentAdminThunk(licId) as any);
    }
  }, [currentStep, licId, dispatch]);

  useEffect(() => {
    if (currentStep === 5 && licId) {
      dispatch(fetchLicOtherPolicyAdminThunk(licId) as any);
    }
  }, [currentStep, licId, dispatch]);

  useEffect(() => {
    if (currentStep === 6 && licId) {
      dispatch(fetchLicNomineeAdminThunk(licId) as any);
    }
  }, [currentStep, licId, dispatch]);

  const lastAppliedNomineeIdRef = React.useRef<number | null>(null);
  useEffect(() => {
    const list = Array.isArray(nomineeData)
      ? nomineeData.filter((row: any) => licId == null || row?.lic_id == null || Number(row.lic_id) === Number(licId))
      : [];
    if (list.length === 0) {
      lastAppliedNomineeIdRef.current = null;
      return;
    }
    const first = list[0];
    const id = first?.id != null ? Number(first.id) : -1;
    if (lastAppliedNomineeIdRef.current === id) return;
    lastAppliedNomineeIdRef.current = id;
    const mapped = mapNomineeDetailsFromResponse(list);
    if (mapped.length === 0) return;
    formik.setValues((prev) => ({ ...prev, nomineeDetails: mapped }));
    if (Number.isFinite(id) && id > 0) dispatch(setNomineeId(id));
  }, [nomineeData, licId]);

  useEffect(() => {
    if (currentStep === 7 && licId) {
      dispatch(fetchLicHealthAdminThunk(licId) as any);
    }
  }, [currentStep, licId, dispatch]);

  // Step 7: When listlichealth_admin returns data, prefill form from response.data[0] (once per fetch)
  const lastAppliedHealthIdRef = React.useRef<number | null>(null);
  useEffect(() => {
    const list = Array.isArray(healthData)
      ? healthData.filter((row: any) => licId == null || row?.lic_id == null || Number(row.lic_id) === Number(licId))
      : [];
    if (list.length === 0) {
      lastAppliedHealthIdRef.current = null;
      return;
    }
    const first = list[0];
    const id = first?.id != null ? Number(first.id) : -1;
    if (lastAppliedHealthIdRef.current === id) return;
    lastAppliedHealthIdRef.current = id;
    const mapped = mapHealthDataToFormValues(first);
    if (Object.keys(mapped).length === 0) return;
    formik.setValues((prev) => ({ ...prev, ...mapped }));
  }, [healthData, licId]);

  useEffect(() => {
    if (currentStep === 8 && licId) {
      dispatch(fetchLicLegalAdminThunk(licId) as any);
    }
  }, [currentStep, licId, dispatch]);

  const lastAppliedLegalIdRef = React.useRef<number | null>(null);
  useEffect(() => {
    const list = Array.isArray(legalData)
      ? legalData.filter((row: any) => licId == null || row?.lic_id == null || Number(row.lic_id) === Number(licId))
      : [];
    if (list.length === 0) {
      lastAppliedLegalIdRef.current = null;
      return;
    }
    const first = list[0];
    const id = first?.id != null ? Number(first.id) : -1;
    if (lastAppliedLegalIdRef.current === id) return;
    lastAppliedLegalIdRef.current = id;
    const toNum01 = (v: any): 0 | 1 => (v === 1 || v === "1" ? 1 : 0);
    const toStr = (v: any) => (v != null && v !== "" ? String(v) : "");
    formik.setValues((prev) => ({
      ...prev,
      declartion_accepted: toNum01(first.declartion_accepted),
      notfatca_holding: toNum01(first.notfatca_holding),
      isverify_consent: toNum01(first.isverify_consent),
      digital_sign: toStr(first.digital_sign),
      date_of_sign: toDateStr(first.date_of_sign) ?? toStr(first.date_of_sign),
      placeof_sign: toStr(first.placeof_sign),
    }));
  }, [legalData, licId]);

  // Step 9: Fetch documents via listlicdocuments_admin (POST) with document_id: 2 and current lic_id
  useEffect(() => {
    if (currentStep === 9 && licId != null) {
      dispatch(
        fetchLicDocumentsAdminThunk({
          lic_id: licId,
          document_id: 0,
          document_typename: "",
          status: "Active",
        }) as any
      );
    }
  }, [currentStep, licId, dispatch]);

  const lastAppliedDocsKeyRef = React.useRef<string | null>(null);
  useEffect(() => {
    const list = Array.isArray(documentData)
      ? documentData.filter((row: any) => licId == null || row?.lic_id == null || Number(row.lic_id) === Number(licId))
      : [];
    if (list.length === 0) {
      lastAppliedDocsKeyRef.current = null;
      return;
    }
    const key = list.map((row: any) => row?.id).join(",");
    if (lastAppliedDocsKeyRef.current === key) return;
    lastAppliedDocsKeyRef.current = key;
    const mapped = mapStep9DocumentsFromList(list);
    if (Object.keys(mapped).length === 0) return;
    formik.setValues((prev) => ({ ...prev, ...mapped }));
  }, [documentData, licId]);

  const validationSchema = React.useMemo(
    () => getValidationSchema(currentStep, skipMandatory),
    [currentStep, skipMandatory]
  );

  const formik = useFormik<ProposalFormValues>({
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      await handleFinalSubmit(values);
    },
  });

  React.useEffect(() => {
    lastPrefilledProposalIdRef.current = null;
    lastAppliedOtherPolicyKeyRef.current = null;
    lastAppliedNomineeIdRef.current = null;
    lastAppliedHealthIdRef.current = null;
    lastAppliedLegalIdRef.current = null;
    lastAppliedDocsKeyRef.current = null;
    formik.resetForm({ values: initialValues });
  }, [proposalIdFromUrl]);

  React.useEffect(() => {
    if (!proposalIdFromUrl) {
      lastPrefilledProposalIdRef.current = null;
      lastAppliedOtherPolicyKeyRef.current = null;
      return;
    }
    if (!licDetailData) return;
    const raw = (licDetailData as any)?.data != null ? (licDetailData as any).data : licDetailData;
    const loadedProposalId = raw?.id ?? raw?.proposal_id;
    if (loadedProposalId != null && String(loadedProposalId) !== String(proposalIdFromUrl)) return;

    const matchingOtherPolicy = Array.isArray(otherPolicyData)
      ? otherPolicyData.filter((row: any) => licId == null || row?.lic_id == null || Number(row.lic_id) === Number(licId))
      : [];

    if (lastPrefilledProposalIdRef.current !== proposalIdFromUrl) {
      lastPrefilledProposalIdRef.current = proposalIdFromUrl;
      const mapped = mapLicDetailToFormValues(licDetailData);
      const next = { ...initialValues };
      Object.keys(mapped).forEach((k) => {
        const v = (mapped as any)[k];
        if (v !== undefined) (next as any)[k] = v;
      });
      if (matchingOtherPolicy.length > 0) {
        const fromList = mapOtherPoliciesToStep5({ otherpolicies: matchingOtherPolicy });
        Object.keys(fromList).forEach((k) => {
          const v = (fromList as any)[k];
          if (v !== undefined && v !== "") (next as any)[k] = v;
        });
        const key = `${licId ?? ""}-${matchingOtherPolicy[0]?.id ?? ""}`;
        lastAppliedOtherPolicyKeyRef.current = key;
      }
      formik.setValues(next);
      return;
    }

    if (matchingOtherPolicy.length === 0) return;
    const key = `${licId ?? ""}-${matchingOtherPolicy[0]?.id ?? ""}`;
    if (lastAppliedOtherPolicyKeyRef.current === key) return;
    lastAppliedOtherPolicyKeyRef.current = key;
    const fromList = mapOtherPoliciesToStep5({ otherpolicies: matchingOtherPolicy });
    formik.setValues((prev) => {
      const next = { ...prev };
      Object.keys(fromList).forEach((k) => {
        const v = (fromList as any)[k];
        if (v !== undefined && v !== "") (next as any)[k] = v;
      });
      return next;
    });
  }, [licDetailData, otherPolicyData, proposalIdFromUrl, licId]);

  React.useEffect(() => {
    formik.validateForm();
  }, [currentStep, skipMandatory]);

  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    if (skipMandatory) return true;

    const errors = await formik.validateForm();
    const stepFields = getStepFields(currentStep);
    
    const hasStepErrors = stepFields.some((field) => {
      const error = (errors as any)[field];
      const value = formik.values[field as keyof ProposalFormValues];
      const isEmpty = value === "" || value === null || value === undefined;
      return !!error || isEmpty;
    });

    if (hasStepErrors) {
      stepFields.forEach((field) => {
        formik.setFieldTouched(field, true, false);
      });
      toast.error("Please fill all required fields before proceeding.");
      return false;
    }

    return true;
  }, [currentStep, skipMandatory, formik]);

  const getStepFields = (step: number): string[] => {
    const stepFieldsMap: Record<number, string[]> = {
      1: [
        "inward_number",
        "date_of_inward",
        "date_of_proposal",
        "consultant_name",
        "consultant_code",
        "chief_agent_name",
        "chief_agent_number",
        "proposal_deposit_currency",
        "proposal_deposit_amount",
        "proposal_number",
      ],
      2: [
        "first_name",
        "last_name",
        "nationality",
        "country_of_birth",
        "place_of_birth",
        "date_of_birth",
        "gender",
        "address",
      ],
      3: [
        "visaissue_country",
        "passport_copy",
        "emirates_id_copy",
      ],
      4: [
        "occupation",
        "industry_type",
        "employername",
        "income_currency",
        "total_anual_income",
        "employer_address",
        "income_source",
        "risk_assessment",
      ],
      5: [
        "bank_account_details",
      ],
      6: [
        // Nominee fields are optional
      ],
      7: [
        "height",
        "weight",
        "smoking_drinking_habits",
      ],
      8: [
        "declartion_accepted",
        "notfatca_holding",
        "isverify_consent",
        "date_of_sign",
        "placeof_sign",
      ],
      9: [
        "identity_proof",
        "address_proof",
        "income_proof",
        "review_confirmation",
      ],
    };
    return stepFieldsMap[step] || [];
  };

  const navigate = useNavigate();

  const persistCurrentStepAndMove = useCallback(async (direction: "next" | "prev") => {
    if (direction === "prev" && currentStep <= 1) return;
    if (direction === "prev" && currentStep === 9) {
      setCurrentStep(8);
      return;
    }
    if (direction === "next") {
      const isValid = await validateCurrentStep();
      if (!isValid) return;
    }

    try {
    // Step 1: Save proposal details via updatenewlicproposal_admin (Update only; proposal_id from URL or state)
    if (currentStep === 1) {
      const v = formik.values;
      const proposalIdParam = searchParams.get("proposal_id");
      const proposalIdForStep1 = proposalIdParam ? Number(proposalIdParam) : proposalId ?? null;
      if (proposalIdForStep1 == null || !Number.isFinite(proposalIdForStep1)) {
        toast.error("Proposal ID is required to save proposal details.");
        return;
      }
      const step1Payload = {
        inward_number: String(v.inward_number ?? ""),
        date_of_inward: String(v.date_of_inward ?? ""),
        date_of_proposal: String(v.date_of_proposal ?? ""),
        consultant_name: String(v.consultant_name ?? ""),
        consultant_code: String(v.consultant_code ?? ""),
        chief_agent_name: String(v.chief_agent_name ?? ""),
        chief_agent_number: String(v.chief_agent_number ?? ""),
        proposal_deposit_currency: String(v.proposal_deposit_currency ?? ""),
        proposal_deposit_amount: v.proposal_deposit_amount ?? "",
        proposal_number: String(v.proposal_number ?? ""),
        depositamttype: String(v.depositamttype ?? ""), // only from depositamttype field, not payment_mode
      };
      const result = await dispatch(
        updateNewLicProposalAdminThunk(proposalIdForStep1, step1Payload) as any
      );
      if (!result?.success) {
        toast.error(result?.message ?? "Failed to save proposal details.");
        return;
      }
      toast.success(result?.message ?? "Proposal details saved.");
    }

    // Step 2: Personal Info — ADD (addpersonalinfo_admin) or UPDATE (updatpersonalinfo_admin?lic_id=)
    if (currentStep === 2) {
      const v = formik.values;
      const proposalIdForPayload = searchParams.get("proposal_id") ? Number(searchParams.get("proposal_id")) : proposalId ?? 1;
      const personalPayload = {
        proposal_id: Number.isFinite(proposalIdForPayload) ? proposalIdForPayload : 1,
        first_name: String(v.first_name ?? ""),
        middle_name: String(v.middle_name ?? ""),
        last_name: String(v.last_name ?? ""),
        father_full_name: String(v.father_full_name ?? ""),
        nationality: String(v.nationality ?? ""),
        country_of_birth: String(v.country_of_birth ?? ""),
        place_of_birth: String(v.place_of_birth ?? ""),
        date_of_birth: String(v.date_of_birth ?? ""),
        gender: String(v.gender ?? ""),
        address: String(v.address ?? ""),
        permanent_address: String(v.permanent_address ?? ""),
        local_country_code: String(v.local_country_code ?? ""),
        local_mobile_number: String(v.local_mobile_number ?? ""),
        local_office_number: String(v.local_office_number ?? ""),
        local_residence_number: String(v.local_residence_number ?? ""),
        home_country_code: String(v.home_country_code ?? ""),
        home_mobile_number: String(v.home_mobile_number ?? ""),
        home_office_number: String(v.home_office_number ?? ""),
        home_residence_number: String(v.home_residence_number ?? ""),
        homeemail: String(v.homeemail ?? ""),
        premium_amount: 1,
        sum_assured: 2,
        planname: "test",
      };
      const useUpdate = shouldUpdate(licId);
      const result = await dispatch(
        useUpdate
          ? (updatePersonalInfoAdminThunk(licId!, personalPayload) as any)
          : (addPersonalInfoAdminThunk(personalPayload) as any)
      );
      if (!result?.success) {
        toast.error(result?.message ?? "Failed to save personal info.");
        return;
      }
      toast.success(result?.message ?? (useUpdate ? "Personal info updated." : "Personal info saved."));
    }

    // Step 3: Identification — ADD (addlicidentification_admin) or UPDATE (updatelicidentification_admin?identification_id=)
    if (currentStep === 3) {
      if (!licId) {
        if (direction === "prev") {
          setCurrentStep(currentStep - 1);
          return;
        }
        toast.error("Please complete Step 2 (Personal Info) first to get LIC id.");
        return;
      }
      const v = formik.values;
      const toIdentDate = (val: unknown): string | null => {
        const s = String(val ?? "").trim();
        if (!s) return null;
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
        const yearMonth = s.match(/^(\d{4})-(\d{2})$/);
        if (yearMonth) return `${yearMonth[1]}-${yearMonth[2]}-01`;
        const monthYear = s.match(/^(\d{1,2})[/-](\d{4})$/);
        if (monthYear) return `${monthYear[2]}-${monthYear[1].padStart(2, "0")}-01`;
        return s.slice(0, 10);
      };
      const identificationPayload = {
        passsportnumber: String(v.passsportnumber ?? ""),
        passport_doe: toIdentDate(v.passport_doe),
        countryofissue: String(v.countryofissue ?? ""),
        citizenshipnumber: String(v.citizenshipnumber ?? ""),
        citizenship_doe: toIdentDate(v.citizenship_doe),
        visaissue_country: String(v.visaissue_country ?? ""),
        visa_doe: toIdentDate(v.visa_doe),
      };
      const useUpdate = shouldUpdate(identificationId);
      if (useUpdate) {
        const result = await dispatch(
          updateLicIdentificationAdminThunk(identificationId!, {
            ...identificationPayload,
            status: "Active",
          }) as any
        );
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to update identification.");
          return;
        }
        toast.success(result?.message ?? "Identification updated.");
      } else {
        const result = await dispatch(
          addLicIdentificationAdminThunk({
            lic_id: licId,
            ...identificationPayload,
          }) as any
        );
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to save identification.");
          return;
        }
        toast.success(result?.message ?? "Identification saved.");
      }
    }

    // Step 4: Employment — ADD (addlicemployment_admin) or UPDATE (updatelicemployment_admin?employment_id=)
    if (currentStep === 4) {
      if (!licId) {
        if (direction === "prev") {
          setCurrentStep(currentStep - 1);
          return;
        }
        toast.error("Please complete Personal Info (Step 2) first to get lic_id.");
        return;
      }
      const v = formik.values;
      const employmentPayload = {
        occupation: String(v.occupation ?? ""),
        industry_type: String(v.industry_type ?? ""),
        employername: String(v.employername ?? ""),
        income_currency: String(v.income_currency ?? ""),
        total_anual_income: Number(v.total_anual_income) || 0,
        employer_address: String(v.employer_address ?? ""),
        income_source: String(v.income_source ?? ""),
        isoccupationrisk: v.risk_assessment === "Yes" ? 1 : 0, // 1 = Yes, 0 = No
        occupationremarks: String(v.occupationremarks ?? ""),
      };
      const useUpdate = shouldUpdate(employmentId ?? employmentData?.[0]?.id);
      const employmentIdToUse = employmentId ?? employmentData?.[0]?.id;
      if (useUpdate && employmentIdToUse != null) {
        const result = await dispatch(
          updateLicEmploymentAdminThunk(employmentIdToUse, {
            ...employmentPayload,
            status: "Active",
          }) as any
        );
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to update employment.");
          return;
        }
        toast.success(result?.message ?? "Employment updated successfully.");
      } else {
        const result = await dispatch(
          addLicEmploymentAdminThunk({
            lic_id: licId,
            ...employmentPayload,
          }) as any
        );
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to save employment.");
          return;
        }
        toast.success(result?.message ?? "Employment saved successfully.");
      }
    }

    // Step 5: Insurance History — send single payload with all required keys (body.premium_waiver, body.family_protection, body.gov_position_hold, body.is_bankacccount, body.otherpolicy_detail) from form
    if (currentStep === 5) {
      if (!licId) {
        if (direction === "prev") {
          setCurrentStep(currentStep - 1);
          return;
        }
        toast.error("Please complete earlier steps first to get LIC id.");
        return;
      }
      const v = formik.values;
      const insurance_plans = Array.isArray(v.insurance_plans) ? v.insurance_plans : [];
      const toDateOnly = (val: unknown): string => {
        const s = String(val ?? "").trim();
        if (!s) return "";
        return s.slice(0, 10);
      };
      const toDateDDMMYYYY = (val: unknown): string => {
        const s = String(val ?? "").trim();
        if (!s) return "";
        const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) return `${match[3]}/${match[2]}/${match[1]}`;
        return s.slice(0, 10);
      };
      const toValidNumber = (val: unknown): number => {
        const n = Number(val);
        return Number.isFinite(n) ? n : 0;
      };
      // otherpolicy_detail: array of form plan details (required by API)
      const otherpolicy_detail = insurance_plans.map((plan: any) => ({
        planname: String(plan?.planname ?? "").trim() || "",
        policy_term: String(plan?.policy_term ?? "").trim() || "",
        premium_term: String(plan?.premium_term ?? "").trim() || "",
        payment_mode: String(plan?.payment_mode ?? "").trim() || "",
        topup_amount: toValidNumber(plan?.topup_amount),
        sum_asured: toValidNumber(plan?.sum_asured),
        premium_amount: toValidNumber(plan?.premium_amount),
        double_accident_befit: String(plan?.double_accident_befit ?? "").trim() || "",
        dateofcommencement: toDateOnly(plan?.dateofcommencement),
      }));
      // Payload matching API: premium_waiver, family_protection, gov_position_hold, is_bankacccount, otherpolicy_detail (required)
      const insuranceDto = {
        lic_id: licId,
        premium_waiver: String(v.premium_waiver_benefit ?? "").trim(),
        family_protection: String(v.family_protection_benefit ?? "").trim(),
        gov_position_hold: String(v.close_relatives_position ?? "").trim(),
        person_name: String(v.relative_name ?? "").trim(),
        relationship: String(v.relative_relationship ?? "").trim(),
        position_hold: String(v.relative_position ?? "").trim(),
        is_bankacccount: String(v.bank_account_details ?? "").trim(),
        bank_name: String(v.bank_name ?? "").trim(),
        branch_name: String(v.bank_branch ?? "").trim(),
        iban_number: String(v.iban ?? "").trim(),
        account_number: String(v.account_number ?? "").trim(),
        uae_cusomer_que: String(v.uae_bank_details ?? "").trim(),
        otherpolicy_detail,
      };
      const useUpdate = shouldUpdate(otherPolicyId);
      if (useUpdate && otherPolicyId != null) {
        const result = await dispatch(
          updateLicOtherPolicyAdminThunk(otherPolicyId, {
            ...insuranceDto,
            status: "Active",
          }) as any
        );
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to update insurance history.");
          return;
        }
        toast.success(result?.message ?? "Insurance history updated.");
      } else {
        const result = await dispatch(addLicInsuranceHistoryAdminThunk(insuranceDto) as any);
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to save insurance history.");
          return;
        }
        toast.success(result?.message ?? "Insurance history saved.");
      }
    }

    // Step 6: Nominee — payload format matches API: lic_id, nominee_name, relationship, nominee_dob, nominee_nationality, passport_*, nominee_identity_*, country_code, nominee_*_contactno, nominee_address, is_minor (0|1), appointee_* (strings or null for dates)
    if (currentStep === 6) {
      if (!licId) {
        if (direction === "prev") {
          setCurrentStep(currentStep - 1);
          return;
        }
        toast.error("Please complete earlier steps first to get LIC id.");
        return;
      }
      // Use form data (nomineeDetails synced from Step6) for payload; when editing with existing nomineeData, prefer it if form is empty
      const formNominees = Array.isArray(formik.values.nomineeDetails) ? formik.values.nomineeDetails : [];
      const hasFormData = formNominees.length > 0 && String(formNominees[0]?.nominee_name ?? "").trim() !== "";
      const nomineeList = hasFormData ? formNominees : (nomineeData as any[] ?? []);
      const first = nomineeList[0];
      if (!first) {
        if (direction === "prev") {
          setCurrentStep(currentStep - 1);
          return;
        }
        toast.error("Please fill nominee details.");
        return;
      }
      // Build single nominee payload with exact keys (null for optional empty dates, is_minor 0|1)
      const toStr = (x: unknown) => (x != null && x !== "" ? String(x) : "");
      const toStrNull = (x: unknown): string | null => (x != null && x !== "" ? String(x) : null);
      const isMinorNum = (x: unknown) => (x === 1 || x === "1" || x === "Yes" ? 1 : 0);
      const addPayload = {
        lic_id: licId,
        nominee_name: toStr(first.nominee_name),
        relationship: toStr(first.relationship),
        nominee_dob: toStr(first.nominee_dob),
        nominee_nationality: toStr(first.nominee_nationality),
        passport_number: toStr(first.passport_number),
        passport_expiry: toStr(first.passport_expiry),
        nominee_identity_no: toStr(first.nominee_identity_no),
        nominee_identity_expiry: toStrNull(first.nominee_identity_expiry),
        country_code: toStr(first.country_code) || "+91",
        nominee_local_contactno: toStr(first.nominee_local_contactno),
        nominee_mobileno: toStr(first.nominee_mobileno),
        nominee_address: toStr(first.nominee_address),
        is_minor: isMinorNum(first.is_minor),
        appointee_name: toStr(first.appointee_name),
        realtion_with_nominee: toStr(first.realtion_with_nominee),
        appointee_dob: toStrNull(first.appointee_dob),
        appointee_nationality: toStr(first.appointee_nationality),
        appointee_passportno: toStr(first.appointee_passportno),
        appointee_passport_expiry: toStrNull(first.appointee_passport_expiry),
        appointee_identityno: toStr(first.appointee_identityno),
        appintee_identity_expiry: toStrNull(first.appintee_identity_expiry),
        appointee_countycode: toStr(first.appointee_countycode),
        appointee_mobileno: toStr(first.appointee_mobileno),
        appointee_address: toStr(first.appointee_address),
        appintee_digital_sign: toStr(first.appintee_digital_sign),
      };
      const useUpdate = shouldUpdate(nomineeId);
      if (useUpdate && nomineeId != null) {
        const { lic_id: _id, ...rest } = addPayload;
        const updatePayload = { ...rest, status: "Active" };
        const result = await dispatch(updateLicNomineeAdminThunk(nomineeId, updatePayload) as any);
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to update nominee.");
          return;
        }
        toast.success(result?.message ?? "Nominee updated.");
      } else {
        const result = await dispatch(addLicNomineeAdminThunk(addPayload) as any);
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to save nominee.");
          return;
        }
        toast.success(result?.message ?? "Nominee saved.");
      }
    }

    // Step 7: Health — ADD (addlichealth_admin) or UPDATE (updatelichealth_admin?health_id=)
    if (currentStep === 7) {
      if (!licId) {
        if (direction === "prev") {
          setCurrentStep(currentStep - 1);
          return;
        }
        toast.error("Please complete earlier steps first to get LIC id.");
        return;
      }
      const v = formik.values;
      const toHealthDate = (val: unknown): string | null => {
        const s = String(val ?? "").trim();
        return s ? s.slice(0, 10) : null;
      };
      const healthPayload = {
        lic_id: licId,
        height: String(v.height ?? "").trim(),
        weight: String(v.weight ?? "").trim(),
        bmi: String(v.bmi ?? "").trim(),
        is_smoke: v.smoking_drinking_habits === "Yes" ? 1 : v.smoking_drinking_habits === "No" ? 0 : 0,
        smokingstop_date: toHealthDate(v.smokingstop_date),
        medicalhistory: String(v.medicalhistory ?? "").trim(),
        medical_his_detail: String(v.medical_his_detail ?? "").trim(),
        dateof_diagnosis: toHealthDate(v.dateof_diagnosis),
        treatment_status: String(v.treatment_status ?? "").trim(),
        family_medical_history: String(v.family_medical_history ?? "").trim(),
        is_preganant: v.is_preganant ?? 0,
      };
      const useUpdate = shouldUpdate(healthId);
      if (useUpdate && healthId != null) {
        const { lic_id: _h, ...rest } = healthPayload;
        const result = await dispatch(updateLicHealthAdminThunk(healthId, { ...rest, status: "Active" }) as any);
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to update health details.");
          return;
        }
        toast.success(result?.message ?? "Health details updated.");
      } else {
        const result = await dispatch(addLicHealthAdminThunk(healthPayload) as any);
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to save health details.");
          return;
        }
        toast.success(result?.message ?? "Health details saved.");
      }
    }

    // Step 8: Legal (Declaration) — ADD (addliclegal_admin) or UPDATE (updateliclegal_admin?legal_id=)
    if (currentStep === 8) {
      if (!licId) {
        if (direction === "prev") {
          setCurrentStep(currentStep - 1);
          return;
        }
        toast.error("Please complete earlier steps first to get LIC id.");
        return;
      }
      const v = formik.values;
      const legalPayload = {
        lic_id: licId,
        declartion_accepted: v.declartion_accepted ?? 0,
        notfatca_holding: v.notfatca_holding ?? 0,
        isverify_consent: v.isverify_consent ?? 0,
        digital_sign: String(v.digital_sign ?? ""),
        date_of_sign: String(v.date_of_sign ?? ""),
        placeof_sign: String(v.placeof_sign ?? ""),
      };
      const useUpdate = shouldUpdate(legalId);
      if (useUpdate && legalId != null) {
        const { lic_id: _l, ...rest } = legalPayload;
        const result = await dispatch(updateLicLegalAdminThunk(legalId, { ...rest, status: "Active" }) as any);
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to update legal/declaration.");
          return;
        }
        toast.success(result?.message ?? "Declaration updated.");
      } else {
        const result = await dispatch(addLicLegalAdminThunk(legalPayload) as any);
        if (!result?.success) {
          toast.error(result?.message ?? "Failed to save legal/declaration.");
          return;
        }
        toast.success(result?.message ?? "Declaration saved.");
      }
    }

    // Step 9: Final submit — only advance (close/reset) when API reports success
    if (currentStep === 9) {
      const success = await handleFinalSubmit(formik.values);
      if (success) {
        formik.resetForm({ values: initialValues });
        setCurrentStep(1);
        dispatch(resetStepperIds());
        navigate("/kyc/personal-details", { replace: true });
      }
      return;
    }

    if (direction === "next" && currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else if (direction === "prev" && currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
    } catch (error: any) {
      const message = error?.message ?? error?.response?.data?.message ?? "Something went wrong. Please try again.";
      toast.error(message);
    }
  }, [currentStep, validateCurrentStep, formik.values, proposalId, licId, identificationId, identificationData, employmentId, employmentData, otherPolicyId, nomineeId, nomineeData, healthId, legalId, dispatch, searchParams, navigate, initialValues]);

  const handleNext = useCallback(async () => {
    await persistCurrentStepAndMove("next");
  }, [persistCurrentStepAndMove]);

  const handlePrevious = useCallback(async () => {
    await persistCurrentStepAndMove("prev");
  }, [persistCurrentStepAndMove]);

  const handleStepClick = useCallback(
    async (step: number) => {
      if (step <= currentStep) {
        // Allow going back to previous steps
        setCurrentStep(step);
      } else {
        // Validate current step before moving forward
        const isValid = await validateCurrentStep();
        if (isValid) {
          setCurrentStep(step);
        }
      }
    },
    [currentStep, validateCurrentStep]
  );

  const handleSaveDraft = useCallback(async () => {
    try {
      // Save to sessionStorage for now
      sessionStorage.setItem("proposal_draft", JSON.stringify(formik.values));
      toast.success("Draft saved successfully!");
      
      // TODO: Replace with API call when ready
      // await saveProposalDraftAPI(formik.values);
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft. Please try again.");
    }
  }, [formik.values]);

  const handleFinalSubmit = useCallback(async (values: ProposalFormValues): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...values,
        date_of_inward: values.date_of_inward
          ? new Date(values.date_of_inward).toISOString()
          : "",
        date_of_proposal: values.date_of_proposal
          ? new Date(values.date_of_proposal).toISOString()
          : "",
      };

      // Save to sessionStorage for now
      sessionStorage.setItem("proposal_preview", JSON.stringify(payload));
      
      // TODO: Replace with API call when ready; then use API response success/message
      // const result = await submitProposalAPI(payload);
      // if (!result?.success) { toast.error(result?.message ?? "Failed to submit."); return false; }
      // toast.success(result?.message ?? "Proposal submitted successfully!");
      
      toast.success("Proposal submitted successfully!");
      return true;
    } catch (error) {
      console.error("Error submitting proposal:", error);
      toast.error("Failed to submit proposal. Please try again.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const renderStepContent = () => {
    const stepProps = {
      values: formik.values,
      errors: formik.errors,
      touched: formik.touched,
      handleChange: formik.handleChange,
      handleBlur: formik.handleBlur,
      setFieldValue: formik.setFieldValue,
      setFieldTouched: formik.setFieldTouched,
      licId,
    };

    switch (currentStep) {
      case 1:
        return <Step1ProposalDetails {...stepProps} />;
      case 2:
        return <Step2PersonalInfo {...stepProps} />;
      case 3:
        return <Step3Identification {...stepProps} />;
      case 4:
        return <Step4Employment {...stepProps} />;
      case 5:
        return <Step5InsuranceHistory key={proposalIdFromUrl ?? "new"} {...stepProps} />;
      case 6:
        return <Step6Nominee key={proposalIdFromUrl ?? "new"} {...stepProps} />;
      case 7:
        return <Step7HealthDetails {...stepProps} />;
      case 8:
        return <Step8Declaration {...stepProps} />;
      case 9:
        return <Step9Submit {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <ProposalFormLayout
      stepper={
        <ProposalStepper currentStep={currentStep} onStepClick={handleStepClick} />
      }
      footer={
        <ProposalFormFooter
          currentStep={currentStep}
          totalSteps={STEPS.length}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSaveDraft={handleSaveDraft}
          isSubmitting={isSubmitting}
          skipMandatory={skipMandatory}
          onSkipMandatoryChange={setSkipMandatory}
        />
      }
    >
      {renderStepContent()}
    </ProposalFormLayout>
  );
};

export default ProposalFormContainer;

